import type { AuthenticatedRequest } from '@app/rest/rest.lib'
import { authenticatedRequest } from '@app/rest/rest.lib'

import type {
  IGetProgramEnrolmentOptions,
  IProgramInteractionRecord,
  IProgram,
  IProgramEnrolment,
  IProgramId,
  IProgramInteractionId,
  ProgramEnrolmentStatusId,
} from './program.interface'
import type { IPatientId } from '../patient/patient.interface'
import type { PdfTemplate } from '../visit/visit.interface'

function urlWithParams(url: string, paramArray: string[][]) {
  const filteredParamArray = paramArray.filter((it) => {
    return it[1] !== undefined && it[1] !== null
  })
  const paramMap = new URLSearchParams(filteredParamArray)
  const urlParams = paramMap.toString()

  return `${url}&${urlParams}`
}

export type ICreateProgramSuccessResponse = {
  status: string
  program: IProgram
}

export type ICreateProgramErrorResponse = {
  status: string
  error: string
}

export type ICreateProgramResponse =
  | ICreateProgramSuccessResponse
  | ICreateProgramErrorResponse

export function isICreateProgramErrorResponse(
  response: ICreateProgramResponse,
): response is ICreateProgramErrorResponse {
  return 'program' in response && response.program !== undefined
}

export function createProgram(
  name: string,
): AuthenticatedRequest<ICreateProgramResponse> {
  const url = `/api/v2/program`

  return authenticatedRequest<ICreateProgramResponse>({
    method: 'post',
    url,
    data: {
      name,
    },
  })
}

export type IUpdateProgramSuccessResponse = {
  status: string
  program: IProgram
}

export type IUpdateProgramErrorResponse = {
  status: string
  error: string
}

export type IUpdateProgramResponse =
  | IUpdateProgramSuccessResponse
  | IUpdateProgramErrorResponse

export function isIUpdateProgramErrorResponse(
  response: IUpdateProgramResponse,
): response is IUpdateProgramErrorResponse {
  return 'error' in response && response.error !== undefined
}

export function isIUpdateProgramSuccessResponse(
  response: IUpdateProgramResponse,
): response is IUpdateProgramSuccessResponse {
  return 'program' in response && response.program !== undefined
}

export function updateProgram(
  program: IProgram,
): AuthenticatedRequest<IUpdateProgramResponse> {
  const url = urlWithParams(`/api/v2/program?`, [
    ['programId', program.programId],
  ])

  return authenticatedRequest<IUpdateProgramResponse>({
    method: 'put',
    url,
    data: {
      name: program.name,
      interactionCompleteContent: program.interactionCompleteContent,
      interactionInformationContent: program.interactionInformationContent,
      interactionNoticeEmailContent: program.interactionNoticeEmailContent,
      interactionNoticeSmsContent: program.interactionNoticeSmsContent,
      remoteInteractionExpirationPeriod:
        program.remoteInteractionExpirationPeriod,
    },
  })
}

export type IGetProgramsResponse = {
  status: string
  programs: IProgram[]
  pageCount: number
  totalNumberOfRecords: number
}

export function getPrograms(
  pageNumber?: number,
  recordsPerPage?: number,
): AuthenticatedRequest<IGetProgramsResponse> {
  let url = `/api/v2/programs`
  if (pageNumber !== undefined && recordsPerPage !== undefined) {
    url = urlWithParams(`/api/v2/programs?`, [
      ['pageNumber', pageNumber?.toString()],
      ['recordsPerPage', recordsPerPage?.toString()],
    ])
  }

  return authenticatedRequest<IGetProgramsResponse>({
    method: 'get',
    url,
  })
}

export type IGetProgramResponse = {
  status: string
  program: IProgram
}

export function getProgram(
  programId: string,
): AuthenticatedRequest<IGetProgramResponse> {
  const url = urlWithParams(`/api/v2/program?`, [['programId', programId]])

  return authenticatedRequest<IGetProgramResponse>({
    method: 'get',
    url,
  })
}

export type IAddReportTemplateToProgramInteractionResponse = {
  reportTemplate: PdfTemplate
}

export function addReportTemplateToProgramInteraction(
  programInteractionId: string,
  templateId: string,
  label?: string,
): AuthenticatedRequest<IAddReportTemplateToProgramInteractionResponse> {
  const url = `/api/v2/program/interaction/${programInteractionId}/reportTemplate`

  return authenticatedRequest<IAddReportTemplateToProgramInteractionResponse>({
    method: 'post',
    url,
    data: {
      templateId,
      label,
    },
  })
}

export function deleteReportTemplateFromProgramInteraction(
  programInteractionId: string,
  templateId: string,
): AuthenticatedRequest<IAddReportTemplateToProgramInteractionResponse> {
  const url = `/api/v2/program/interaction/${programInteractionId}/reportTemplate/${templateId}`

  return authenticatedRequest<IAddReportTemplateToProgramInteractionResponse>({
    method: 'delete',
    url,
  })
}

export type ICreateProgramInteractionSuccessResponse = {
  status: string
  program: IProgram
}

export type ICreateProgramInteractionErrorResponse = {
  status: string
  message: string
}

export type ICreateProgramInteractionResponse =
  | ICreateProgramInteractionSuccessResponse
  | ICreateProgramInteractionErrorResponse

export function isICreateProgramInteractionErrorResponse(
  response: ICreateProgramInteractionResponse,
): response is ICreateProgramInteractionErrorResponse {
  return 'message' in response && response.message !== undefined
}

export function createProgramInteraction(
  programId: string,
  programInteractionType: string,
  instrumentIds: string[],
): AuthenticatedRequest<ICreateProgramInteractionResponse> {
  const url = `/api/v2/program/interaction`

  return authenticatedRequest<ICreateProgramInteractionResponse>({
    method: 'post',
    url,
    data: {
      programId,
      programInteractionType,
      instrumentIds,
    },
  })
}

export type IUpdateProgramInteractionSuccessResponse = {
  status: string
  program: IProgram
}

export type IUpdateProgramInteractionErrorResponse = {
  status: string
  message: string
}

export type IUpdateProgramInteractionResponse =
  | IUpdateProgramInteractionSuccessResponse
  | IUpdateProgramInteractionErrorResponse

export function isIUpdateProgramInteractionErrorResponse(
  response: IUpdateProgramInteractionResponse,
): response is IUpdateProgramInteractionErrorResponse {
  return 'message' in response && response.message !== undefined
}

export function updateProgramInteraction(
  programInteractionId: string,
  instrumentIds: string[],
): AuthenticatedRequest<IUpdateProgramInteractionResponse> {
  const url = `/api/v2/program/interaction`

  return authenticatedRequest<IUpdateProgramInteractionResponse>({
    method: 'put',
    url,
    data: {
      programInteractionId,
      instrumentIds,
    },
  })
}

export type IUpdateProgramInteractionPropertiesSuccessResponse = {
  status: string
  program: IProgram
}

export type IUpdateProgramInteractionPropertiesErrorResponse = {
  status: string
  message: string
}

export type IUpdateProgramInteractionPropertiesResponse =
  | IUpdateProgramInteractionPropertiesSuccessResponse
  | IUpdateProgramInteractionPropertiesErrorResponse

export function isIUpdateProgramInteractionPropertiesErrorResponse(
  response: IUpdateProgramInteractionPropertiesResponse,
): response is IUpdateProgramInteractionPropertiesErrorResponse {
  return 'message' in response && response.message !== undefined
}

export function updateProgramInteractionProperties(
  programInteractionId: string,
  label?: string,
  daysAfterInitialInteraction?: number,
): AuthenticatedRequest<IUpdateProgramInteractionPropertiesResponse> {
  const url = `/api/v2/program/interaction/${programInteractionId}/properties`

  return authenticatedRequest<IUpdateProgramInteractionResponse>({
    method: 'put',
    url,
    data: { label, daysAfterInitialInteraction },
  })
}

export function startInteraction(
  visitId: string,
  programInteractionIds: string[],
): AuthenticatedRequest<IUpdateProgramInteractionResponse> {
  const url = `/api/v2/visit/${visitId}/interaction`

  return authenticatedRequest<IUpdateProgramInteractionResponse>({
    method: 'post',
    url,
    data: {
      programInteractionIds,
    },
  })
}

export type IGetProgramInteractionHistoryForPatientSuccessResponse = {
  status: string
  history: Record<IProgramInteractionId, Date>
}

export type IGetProgramInteractionHistoryForPatientErrorResponse = {
  status: string
  message: string
}

export type IGetProgramInteractionHistoryForPatientResponse =
  | IGetProgramInteractionHistoryForPatientSuccessResponse
  | IGetProgramInteractionHistoryForPatientErrorResponse

export function isIGetProgramInteractionHistoryForPatientErrorResponse(
  response: IGetProgramInteractionHistoryForPatientResponse,
): response is IGetProgramInteractionHistoryForPatientErrorResponse {
  return 'message' in response && response.message !== undefined
}

export function isIGetProgramInteractionHistoryForPatientSuccessResponse(
  response: IGetProgramInteractionHistoryForPatientResponse,
): response is IGetProgramInteractionHistoryForPatientSuccessResponse {
  return 'history' in response && response.history !== undefined
}

export function assertGetProgramInteractionHistoryForPatientSuccessResponse(
  response: IGetProgramInteractionHistoryForPatientResponse,
): asserts response is IGetProgramInteractionHistoryForPatientSuccessResponse {
  if (!('history' in response && response.history !== undefined)) {
    throw new TypeError(
      `Provided value is not isIGetProgramInteractionHistoryForPatientSuccessResponse`,
    )
  }
}

export function getProgramInteractionHistoryForPatient(patientId: string) {
  const url = `/api/v2/program/history?patientId=${patientId}`

  return authenticatedRequest<IGetProgramInteractionHistoryForPatientResponse>({
    method: 'get',
    url,
  })
}

export interface IRemoveProgramInteractionResponse {
  program: IProgram
}

export function removeProgramInteractionHttpRequest(
  programInteractionId: string,
): AuthenticatedRequest<IRemoveProgramInteractionResponse> {
  return authenticatedRequest<IRemoveProgramInteractionResponse>({
    method: 'post',
    url: `/api/v2/program/interaction/${programInteractionId}/remove`,
  })
}

export interface IDeleteProgramInteractionResponse {
  program: IProgram
}

export function deleteProgramInteraction(
  programInteractionId: string,
): AuthenticatedRequest<IDeleteProgramInteractionResponse> {
  const url = `/api/v2/program/interaction/${programInteractionId}`

  return authenticatedRequest<IDeleteProgramInteractionResponse>({
    method: 'delete',
    url,
  })
}

export type IAddPatientsToProgramSuccessResponse = {
  status: string
  program: IProgram
}

export type IAddPatientsToProgramErrorResponse = {
  status: string
  message: string
}

export type IAddPatientsToProgramResponse =
  | IAddPatientsToProgramSuccessResponse
  | IAddPatientsToProgramErrorResponse

export function addPatientsToProgram(
  programId: IProgramId,
  patientIds: IPatientId[],
  startDate: string,
): AuthenticatedRequest<IAddPatientsToProgramResponse> {
  const url = `/api/v2/program/${programId}/patients`

  return authenticatedRequest<IAddPatientsToProgramResponse>({
    method: 'post',
    url,
    data: {
      patientIds,
      startDate,
    },
  })
}

export type IUpdateProgramEnrolmentStatusSuccessResponse = {
  status: string
  program: IProgram
}

export type IUpdateProgramEnrolmentStatusErrorResponse = {
  status: string
  message: string
}

export type UpdateProgramEnrolmentStatusResponse =
  | IUpdateProgramEnrolmentStatusSuccessResponse
  | IUpdateProgramEnrolmentStatusErrorResponse

export function updateProgramEnrolmentStatus(
  programId: IProgramId,
  patientIds: IPatientId[],
  status: ProgramEnrolmentStatusId,
  statusReason: string,
  statusReasonDescription: string,
): AuthenticatedRequest<UpdateProgramEnrolmentStatusResponse> {
  const url = `/api/v2/program/${programId}/update-patient-enrolment-status`

  return authenticatedRequest<UpdateProgramEnrolmentStatusResponse>({
    method: 'post',
    url,
    data: {
      patientIds,
      status,
      statusReason,
      statusReasonDescription,
    },
  })
}

export type IDeleteProgramEnrolmentSuccessResponse = {
  status: string
}

export type IDeleteProgramEnrolmentErrorResponse = {
  status: string
  message: string
}

export type DeleteProgramEnrolmentResponse =
  | IDeleteProgramEnrolmentSuccessResponse
  | IDeleteProgramEnrolmentErrorResponse

export function deleteProgramEnrolment(
  programId: IProgramId,
  patientId: IPatientId,
): AuthenticatedRequest<UpdateProgramEnrolmentStatusResponse> {
  const url = `/api/v2/program/${programId}/enrolment/${patientId}`

  return authenticatedRequest<UpdateProgramEnrolmentStatusResponse>({
    method: 'delete',
    url,
  })
}

export type IGetReasonsForDiscontinuationResponse = {
  reasons: string[]
}

export function getReasonsForDiscontinuation() {
  const url = `/api/v2/config/program/reasons`

  return authenticatedRequest<IGetReasonsForDiscontinuationResponse>({
    method: 'get',
    url,
  })
}

export type IListInteractionRecordsForProgramResponse = {
  total: number
  interactions: IProgramInteractionRecord[]
}

export function listInteractionRecordsForProgram(
  programId: IProgramId,
  pageNumber: number,
  recordsPerPage: number,
  options: Partial<{
    patientName: string
    mrn: string
    isDeliverable?: boolean
    status?: string
    sortField: string
    sortDirection: string
  }> = {},
): AuthenticatedRequest<IListInteractionRecordsForProgramResponse> {
  const {
    patientName,
    mrn,
    isDeliverable,
    status,
    sortField,
    sortDirection,
  } = options

  const paramArray: string[][] = [
    ['pageNumber', pageNumber.toString()],
    ['recordsPerPage', recordsPerPage.toString()],
    ['patientName', patientName],
    ['mrn', mrn],
    ['isDeliverable', isDeliverable?.toString()],
    ['status', status?.toString()],
    ['sortField', sortField],
    ['sortDirection', sortDirection],
  ].filter(([_, value]) => value !== undefined && value !== null)

  const urlParams = new URLSearchParams(paramArray).toString()

  const url = `/api/v2/program/${programId}/interaction-records?${urlParams}`

  return authenticatedRequest<IListInteractionRecordsForProgramResponse>({
    method: 'get',
    url,
  })
}

export type IGetProgramStatisticsResponse = {
  enrolledPatients: number
  completedInteractions: number
  pendingInteractions: number
}

export function getProgramStatistics(
  programId: IProgramId,
): AuthenticatedRequest<IGetProgramStatisticsResponse> {
  const url = `/api/v2/program/${programId}/statistics`

  return authenticatedRequest<IGetProgramStatisticsResponse>({
    method: 'get',
    url,
  })
}

export type IUpdateProgramEnrolmentStartDateResponse = {
  status: string
  enrolment: IProgramEnrolment
}

export function updateProgramEnrolmentStartDate(
  programId: IProgramId,
  patientId: IPatientId,
  startDate: string,
): AuthenticatedRequest<IUpdateProgramEnrolmentStartDateResponse> {
  const url = `/api/v2/program/${programId}/enrolment/${patientId}/start-date`

  return authenticatedRequest<IUpdateProgramEnrolmentStartDateResponse>({
    method: 'post',
    url,
    data: { startDate },
  })
}

export type IGetProgramEnrolmentsResponse = {
  status: string
  enrolments: IProgramEnrolment[]
  pageCount: number
  totalNumberOfRecords: number
}

export function getProgramEnrolments(
  programId: IProgramId,
  pageNumber: number,
  recordsPerPage: number,
  options: IGetProgramEnrolmentOptions,
): AuthenticatedRequest<IGetProgramEnrolmentsResponse> {
  const {
    birthSex,
    dateOfBirth,
    deliverable,
    mrn,
    patientName,
    sortDirection,
    sortField,
  } = options
  const paramArray: string[][] = [
    ['dateOfBirth', dateOfBirth],
    ['pageNumber', pageNumber.toString()],
    ['recordsPerPage', recordsPerPage.toString()],
    ['mrn', mrn],
    ['patientName', patientName],
    ['sortDirection', sortDirection],
    ['sortField', sortField],
    ['deliverable', deliverable?.toString()],
  ].filter((it) => {
    return it[1] !== undefined && it[1] !== null
  })

  let birthSexUrlParams = ''
  if (birthSex && birthSex.length > 0) {
    birthSexUrlParams = birthSex.map((it) => `&birthSex=${it}`).join('')
  }

  const urlParams = new URLSearchParams(paramArray).toString()

  const url = `/api/v2/program/${programId}/enrolments?${urlParams}${birthSexUrlParams}`

  return authenticatedRequest<IGetProgramEnrolmentsResponse>({
    method: 'get',
    url,
  })
}

export type IBulkEnrolmentErrorType =
  | 'invalid-mrn'
  | 'invalid-date'
  | 'parsing-error'

export type IBulkEnrolmentError = {
  type: IBulkEnrolmentErrorType
  rowIndex: number
  mrn?: string
}

export type IUploadBulkEnrolmentFileResponse = {
  status: 'success' | 'failure'
  stage: string
  numberEnrolled: number
  numberUpdated: number
  errors: IBulkEnrolmentError[]
}

export function uploadBulkEnrolmentFile(
  file: File,
  programId: string,
): AuthenticatedRequest<IUploadBulkEnrolmentFileResponse> {
  const url = `/api/v2/program/${programId}/bulk-enrolment`
  const formData = new FormData()
  formData.append('csv', file, file.name)

  return authenticatedRequest<IUploadBulkEnrolmentFileResponse>({
    method: 'post',
    url,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  })
}

export type ICreateProgramInteractionWithDefinitionSuccessResponse = {
  status: string
  program: IProgram
}

export type ICreateProgramInteractionWithDefinitionErrorResponse = {
  status: string
  message: string
}

export type ICreateProgramInteractionWithDefinitionResponse =
  | ICreateProgramInteractionWithDefinitionSuccessResponse
  | ICreateProgramInteractionWithDefinitionErrorResponse

export function isICreateProgramInteractionWithDefinitionErrorResponse(
  response: ICreateProgramInteractionWithDefinitionResponse,
): response is ICreateProgramInteractionWithDefinitionErrorResponse {
  return 'message' in response && response.message !== undefined
}

export function createProgramInteractionWithDefinitionId(
  programId: string,
  programInteractionType: string,
  visitDefinitionId: string,
): AuthenticatedRequest<ICreateProgramInteractionWithDefinitionResponse> {
  const url = `/api/v2/program/interaction/with-definition`

  return authenticatedRequest<ICreateProgramInteractionWithDefinitionResponse>({
    method: 'post',
    url,
    data: {
      programId,
      programInteractionType,
      visitDefinitionId,
    },
  })
}

export type IUpdateProgramInteractionWithDefinitionSuccessResponse = {
  status: string
  program: IProgram
}

export type IUpdateProgramInteractionWithDefinitionErrorResponse = {
  status: string
  message: string
}

export type IUpdateProgramInteractionWithDefinitionResponse =
  | IUpdateProgramInteractionWithDefinitionSuccessResponse
  | IUpdateProgramInteractionWithDefinitionErrorResponse

export function isIUpdateProgramInteractionWithDefinitionErrorResponse(
  response: IUpdateProgramInteractionWithDefinitionResponse,
): response is IUpdateProgramInteractionWithDefinitionErrorResponse {
  return 'message' in response && response.message !== undefined
}

export function updateProgramInteractionWithDefinitionIdHttpRequest(
  programInteractionId: string,
  visitDefinitionId: string,
): AuthenticatedRequest<IUpdateProgramInteractionWithDefinitionResponse> {
  const url = `/api/v2/program/interaction/${programInteractionId}/definition`

  return authenticatedRequest<IUpdateProgramInteractionWithDefinitionResponse>({
    method: 'put',
    url,
    data: {
      visitDefinitionId,
    },
  })
}
